import React, { useMemo, useState } from 'react';
import { Box, FormControlLabel, Switch, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { formatCompact } from './ui/charts';

interface PillarMetric {
  avg_engagement: number;
  sum_engagement: number;
  post_count: number;
}

interface PillarMatrixProps {
  /** brand -> pillar -> metrics, already filtered to the selected platforms */
  data: Record<string, Record<string, PillarMetric | undefined>>;
  brands: string[];
  pillars: string[];
  metric: 'average' | 'total';
  getBrandColor: (brand: string) => string;
}

// Same five-step plum ramp as the Best Time to Post heatmap
const RAMP = [0.1, 0.24, 0.42, 0.62, 0.85];
// How many cells carry a number when "Show all values" is off: about the
// square root of the filled cells (15 or so for a 26 x 10 grid), 6 to 20.
const anchorCountFor = (cells: number) => Math.max(6, Math.min(20, Math.round(Math.sqrt(cells))));

const valueOf = (m: PillarMetric | undefined, metric: 'average' | 'total') =>
  !m || !m.post_count ? null : metric === 'average' ? m.avg_engagement : m.sum_engagement;

/** Round to 1, 1.5, 2, 3, 5 or 7.5 times a power of ten (1M, 150k, 20k...). */
const niceRound = (v: number) => {
  if (v <= 0) return 0;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const steps = [1, 1.5, 2, 3, 5, 7.5, 10];
  const m = v / pow;
  const best = steps.reduce((a, b) => (Math.abs(b - m) < Math.abs(a - m) ? b : a));
  return best * pow;
};

/**
 * Brands down the side, pillars across the top, one shared shading scale.
 * Only some "anchor" cells show their number by default (cells near round
 * values spread across the range, e.g. 1M, 150k, 20k, and across the grid), so
 * the grid reads as a picture; a switch shows every value. A corner dot marks
 * the leading brand in each pillar.
 */
const PillarMatrix: React.FC<PillarMatrixProps> = ({ data, brands, pillars, metric, getBrandColor }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [hover, setHover] = useState<{ brand: string; pillar: string } | null>(null);
  const [showAll, setShowAll] = useState(false);

  const { rows, cols, lo, hi, colRank, colLeader, anchors } = useMemo(() => {
    // Columns: pillars with the most total engagement first.
    const pillarTotal = (p: string) => brands.reduce((s, br) => s + (data[br]?.[p]?.sum_engagement || 0), 0);
    const cols = [...pillars].sort((a, b) => pillarTotal(b) - pillarTotal(a));
    // Rows: brands by their overall figure for the chosen metric.
    const overall = (b: string) => {
      let sum = 0;
      let posts = 0;
      cols.forEach((p) => {
        sum += data[b]?.[p]?.sum_engagement || 0;
        posts += data[b]?.[p]?.post_count || 0;
      });
      return metric === 'average' ? (posts ? sum / posts : 0) : sum;
    };
    const rows = [...brands].sort((a, b) => overall(b) - overall(a));

    // One log-scale range for the whole grid, so a shade means the same
    // amount in every column (and the anchor numbers calibrate all of it).
    const cells: Array<{ b: string; p: string; v: number }> = [];
    rows.forEach((b) => cols.forEach((p) => {
      const v = valueOf(data[b]?.[p], metric);
      if (v !== null && v > 0) cells.push({ b, p, v });
    }));
    const logs = cells.map((c) => Math.log1p(c.v));
    const lo = logs.length ? Math.min(...logs) : 0;
    const hi = logs.length ? Math.max(...logs) : 0;

    const colRank: Record<string, Record<string, number>> = {};
    const colLeader: Record<string, string | undefined> = {};
    cols.forEach((p) => {
      const ranked = cells.filter((c) => c.p === p).sort((x, y) => y.v - x.v);
      colRank[p] = {};
      ranked.forEach((c, i) => { colRank[p][c.b] = i + 1; });
      colLeader[p] = ranked[0]?.b;
    });

    // Anchors: round values spread evenly (in log terms) across the range.
    // For each one, pick among the cells near that value the one whose row
    // and column have the fewest labels so far, so the numbers also spread
    // out across the grid instead of bunching up.
    const anchors = new Set<string>();
    const count = anchorCountFor(cells.length);
    const span = hi - lo;
    const tolerance = count > 1 ? span / (count - 1) / 2 : span;
    const rowUsed: Record<string, number> = {};
    const colUsed: Record<string, number> = {};
    for (let i = 0; i < count && cells.length; i++) {
      const target = Math.log1p(niceRound(Math.expm1(hi - (span * i) / Math.max(1, count - 1))));
      const open = cells.filter((c) => !anchors.has(`${c.b}\u0000${c.p}`));
      if (!open.length) break;
      const near = open.filter((c) => Math.abs(Math.log1p(c.v) - target) <= tolerance);
      const pool = near.length ? near : open;
      let best = pool[0];
      let bestScore = Infinity;
      pool.forEach((c) => {
        const d = Math.abs(Math.log1p(c.v) - target);
        const crowding = (rowUsed[c.b] || 0) + (colUsed[c.p] || 0);
        const score = crowding + (tolerance > 0 ? d / tolerance : d) * 0.5;
        if (score < bestScore) {
          bestScore = score;
          best = c;
        }
      });
      anchors.add(`${best.b}\u0000${best.p}`);
      rowUsed[best.b] = (rowUsed[best.b] || 0) + 1;
      colUsed[best.p] = (colUsed[best.p] || 0) + 1;
    }
    return { rows, cols, lo, hi, colRank, colLeader, anchors };
  }, [data, brands, pillars, metric]);

  const stepOf = (v: number | null) => {
    if (v === null || hi <= 0) return null;
    const ratio = hi > lo ? (Math.log1p(Math.max(0, v)) - lo) / (hi - lo) : 1;
    return Math.min(RAMP.length - 1, Math.floor(ratio * RAMP.length));
  };

  // Value range covered by each legend swatch
  const bounds = Array.from({ length: RAMP.length + 1 }, (_, i) => Math.expm1(lo + ((hi - lo) * i) / RAMP.length));
  const metricLabel = metric === 'average' ? 'avg. engagement per post' : 'total engagement';
  const format = (v: number) => (metric === 'average' && v < 100 ? v.toFixed(1) : formatCompact(v));
  const leaderRing = isDark ? theme.palette.text.primary : theme.ink.ink;

  return (
    <Box>
      <Box sx={{ overflowX: 'auto', pb: 0.5 }} onMouseLeave={() => setHover(null)}>
        <Box
          component="table"
          sx={{
            width: '100%',
            minWidth: 160 + cols.length * 84,
            borderCollapse: 'separate',
            borderSpacing: '3px',
            tableLayout: 'fixed',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <colgroup>
            <col style={{ width: 168 }} />
            {cols.map((p) => (
              <col key={p} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <Box component="th" scope="col" sx={{ textAlign: 'left', verticalAlign: 'bottom', pb: 0.75 }}>
                <Typography variant="caption">Brand</Typography>
              </Box>
              {cols.map((p) => (
                <Box
                  component="th"
                  scope="col"
                  key={p}
                  title={p}
                  sx={{
                    verticalAlign: 'bottom',
                    pb: 0.75,
                    px: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    lineHeight: 1.25,
                    textAlign: 'center',
                    color: hover?.pillar === p ? 'text.primary' : 'text.secondary',
                    transition: 'color 100ms ease',
                  }}
                >
                  {/* Two lines max; the full name is in the title tooltip */}
                  <Box component="span" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {p}
                  </Box>
                </Box>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b}>
                <Box
                  component="th"
                  scope="row"
                  title={b}
                  sx={{
                    textAlign: 'left',
                    fontWeight: hover?.brand === b ? 600 : 500,
                    fontSize: '0.8125rem',
                    pr: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Box component="span" sx={{ display: 'inline-block', width: 9, height: 9, borderRadius: '3px', bgcolor: getBrandColor(b), mr: 1, verticalAlign: 'middle' }} />
                  {b}
                </Box>
                {cols.map((p) => {
                  const m = data[b]?.[p];
                  const v = valueOf(m, metric);
                  const step = stepOf(v);
                  const rank = colRank[p]?.[b];
                  const isLeader = colLeader[p] === b;
                  const isAnchor = anchors.has(`${b}\u0000${p}`);
                  const hovered = hover?.brand === b && hover?.pillar === p;
                  const showNumber = v !== null && (showAll || isAnchor || hovered);
                  const inCrosshair = hover && (hover.brand === b || hover.pillar === p);
                  // Light mode: white text on the two darkest steps. Dark mode: the
                  // ramp gets lighter as it rises, so only the top step needs ink text.
                  const strong = step !== null && (isDark ? step >= 4 : step >= 3);
                  const leaderDot = strong ? (isDark ? '#1B1526' : '#FFFFFF') : leaderRing;
                  return (
                    <Tooltip
                      key={p}
                      placement="top"
                      title={
                        v === null ? (
                          `${b} has no ${p} posts in this period`
                        ) : (
                          <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            <Box sx={{ fontWeight: 600, mb: 0.5 }}>{b}, {p}</Box>
                            <div>Avg. engagement per post: {Math.round(m!.avg_engagement).toLocaleString()}</div>
                            <div>Total engagement: {Math.round(m!.sum_engagement).toLocaleString()}</div>
                            <div>Posts: {m!.post_count.toLocaleString()}</div>
                            <Box sx={{ mt: 0.5, opacity: 0.8 }}>
                              #{rank} of {Object.keys(colRank[p] || {}).length} in this pillar on {metricLabel}
                            </Box>
                          </Box>
                        )
                      }
                    >
                      <Box
                        component="td"
                        onMouseEnter={() => setHover({ brand: b, pillar: p })}
                        aria-label={v === null ? `${b}, ${p}: no posts` : `${b}, ${p}: ${format(v)}`}
                        sx={{
                          height: 34,
                          borderRadius: '6px',
                          textAlign: 'center',
                          fontSize: '0.78rem',
                          fontWeight: isAnchor && !showAll ? 650 : 500,
                          cursor: 'default',
                          color: v === null
                            ? 'text.disabled'
                            : strong
                              ? (isDark ? '#1B1526' : '#FFFFFF')
                              : 'text.primary',
                          bgcolor: step === null
                            ? alpha(theme.palette.text.primary, isDark ? 0.03 : 0.025)
                            : alpha(theme.palette.primary.main, RAMP[step]),
                          position: 'relative',
                          boxShadow: hovered ? `0 0 0 2px ${theme.palette.primary.main}` : 'none',
                          // A small dot in the corner marks the leader of each pillar
                          '&::after': isLeader ? {
                            content: '""',
                            position: 'absolute',
                            top: 5,
                            right: 5,
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: leaderDot,
                          } : undefined,
                          opacity: hover && !inCrosshair ? 0.55 : 1,
                          transition: 'opacity 100ms ease, box-shadow 100ms ease',
                        }}
                      >
                        {showNumber ? format(v as number) : v === null && showAll ? '-' : ''}
                      </Box>
                    </Tooltip>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>

      {/* Legend: what each shade covers, the leader ring, and the numbers switch */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', columnGap: 3, rowGap: 1.5, mt: 1.5 }}>
        <Box>
          <Box sx={{ display: 'flex', gap: '3px' }}>
            {RAMP.map((a, i) => (
              <Box key={a} sx={{ width: 68 }}>
                <Box sx={{ height: 12, borderRadius: '3px', bgcolor: alpha(theme.palette.primary.main, a) }} />
                <Typography variant="caption" component="div" sx={{ fontSize: '0.68rem', mt: 0.25, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {i === 0
                    ? `under ${format(bounds[1])}`
                    : i === RAMP.length - 1
                      ? `${format(bounds[i])} and up`
                      : `${format(bounds[i])} to ${format(bounds[i + 1])}`}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
            {metric === 'average' ? 'Avg. engagement per post' : 'Total engagement'}, one scale for the whole grid
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 0.25 }}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: leaderRing }} />
          <Typography variant="caption">Leader in each pillar</Typography>
        </Box>

        <FormControlLabel
          sx={{ ml: { md: 'auto' }, mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8125rem', color: 'text.secondary' } }}
          control={<Switch size="small" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
          label="Show all values"
        />
      </Box>
    </Box>
  );
};

export default PillarMatrix;
